import { useState } from "react";
import { useMutation } from "convex/react";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../convex/_generated/api";
import type { Doc, Id } from "../../../convex/_generated/dataModel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { AddReactionButton, ReactionPills } from "./ReactionBar";

type HydratedMessage = Doc<"messages"> & {
  author: { _id: Id<"users">; name?: string; image?: string } | null;
  reactions: Array<{ emoji: string; count: number; mine: boolean }>;
  attachmentUrls: Array<{
    storageId: Id<"_storage">;
    name: string;
    contentType: string;
    size: number;
    url: string | null;
  }>;
};

const NAME_COLORS = [
  "text-rose-500",
  "text-amber-500",
  "text-emerald-500",
  "text-sky-500",
  "text-violet-500",
  "text-fuchsia-500",
  "text-teal-500",
  "text-indigo-500",
];

function colorForId(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return NAME_COLORS[Math.abs(h) % NAME_COLORS.length];
}

export function MessageItem({
  message,
  meId,
  conversationKind,
  showHeader,
  isLastOfGroup,
}: {
  message: HydratedMessage;
  meId: Id<"users">;
  conversationKind: "dm" | "room";
  showHeader: boolean;
  isLastOfGroup: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body);
  const edit = useMutation(api.messages.edit);
  const remove = useMutation(api.messages.softDelete);

  const isMine = message.authorId === meId;
  const isDeleted = !!message.deletedAt;
  const initials = (message.author?.name ?? "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const submitEdit = async () => {
    try {
      await edit({ messageId: message._id, body: draft });
      setEditing(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const time = new Date(message._creationTime).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const showAuthorName =
    showHeader && !isMine && conversationKind === "room" && !isDeleted;

  const bubbleBase =
    "rounded-2xl px-3 py-1.5 text-sm shadow-sm break-words relative";
  const bubbleSide = isMine
    ? cn(
        "bg-primary text-primary-foreground",
        isLastOfGroup ? "rounded-br-sm" : "",
      )
    : cn("bg-muted", isLastOfGroup ? "rounded-bl-sm" : "");

  const hasReactions = !isDeleted && message.reactions.length > 0;

  const hoverCluster = (
    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
      {isMine ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-muted-foreground"
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setEditing(true)}>
              <Pencil className="size-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={async () => {
                try {
                  await remove({ messageId: message._id });
                } catch (err) {
                  toast.error((err as Error).message);
                }
              }}
            >
              <Trash2 className="size-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <AddReactionButton
        messageId={message._id}
        align={isMine ? "end" : "start"}
      />
    </div>
  );

  const pills = hasReactions ? (
    <div className="max-w-[40vw]">
      <ReactionPills
        messageId={message._id}
        reactions={message.reactions}
        align={isMine ? "end" : "start"}
      />
    </div>
  ) : null;

  const sideActions =
    !isDeleted && !editing ? (
      <div className="self-center flex items-center gap-1">
        {isMine ? (
          <>
            {hoverCluster}
            {pills}
          </>
        ) : (
          <>
            {pills}
            {hoverCluster}
          </>
        )}
      </div>
    ) : null;

  return (
    <div
      className={cn(
        "group flex items-end gap-2",
        isMine ? "justify-end" : "justify-start",
      )}
    >
      {!isMine ? (
        <div className="w-8 shrink-0">
          {isLastOfGroup ? (
            <Avatar className="size-8">
              {message.author?.image ? (
                <AvatarImage src={message.author.image} />
              ) : null}
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
          ) : null}
        </div>
      ) : null}

      {isMine ? sideActions : null}

      <div
        className={cn(
          "relative max-w-[75%] flex flex-col",
          isMine ? "items-end" : "items-start",
        )}
      >
        {editing ? (
          <div className="space-y-2 w-[min(28rem,75vw)]">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="min-h-[60px]"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submitEdit();
                }
                if (e.key === "Escape") {
                  setEditing(false);
                  setDraft(message.body);
                }
              }}
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditing(false);
                  setDraft(message.body);
                }}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={submitEdit}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className={cn(bubbleBase, bubbleSide)}>
              {showAuthorName ? (
                <div
                  className={cn(
                    "text-xs font-medium mb-0.5",
                    colorForId(message.authorId),
                  )}
                >
                  {message.author?.name ?? "Unknown"}
                </div>
              ) : null}
              {isDeleted ? (
                <p className="italic opacity-70">
                  Message deleted
                  <span className="inline-block w-14" aria-hidden />
                </p>
              ) : (
                <>
                  {message.attachmentUrls.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mb-1 -mx-1">
                      {message.attachmentUrls.map((a) =>
                        a.url ? (
                          a.contentType.startsWith("image/") ? (
                            <a
                              key={a.storageId}
                              href={a.url}
                              target="_blank"
                              rel="noreferrer"
                              className="block"
                            >
                              <img
                                src={a.url}
                                alt={a.name}
                                className="max-h-64 max-w-sm rounded-xl"
                              />
                            </a>
                          ) : (
                            <a
                              key={a.storageId}
                              href={a.url}
                              target="_blank"
                              rel="noreferrer"
                              className={cn(
                                "text-xs underline",
                                isMine
                                  ? "text-primary-foreground/90"
                                  : "text-muted-foreground",
                              )}
                            >
                              {a.name}
                            </a>
                          )
                        ) : null,
                      )}
                    </div>
                  ) : null}
                  {message.body ? (
                    <p className="whitespace-pre-wrap break-words">
                      {message.body}
                      <span
                        className={cn(
                          "inline-block",
                          message.editedAt ? "w-24" : "w-14",
                        )}
                        aria-hidden
                      />
                    </p>
                  ) : null}
                </>
              )}
              <span
                className={cn(
                  "absolute bottom-1 right-2.5 text-[10px] leading-none select-none whitespace-nowrap",
                  isMine
                    ? "text-primary-foreground/70"
                    : "text-muted-foreground",
                )}
              >
                {message.editedAt ? "edited · " : ""}
                {time}
              </span>
            </div>
          </>
        )}
      </div>

      {!isMine ? sideActions : null}
    </div>
  );
}
